import type { GatewayBrowserClient } from "../gateway.ts";
import type { ConfigSchemaResponse, ConfigSnapshot, ConfigUiHints } from "../types.ts";
import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import { loadAgents } from "./agents.js";
import {
  cloneConfigObject,
  removePathValue,
  serializeConfigForm,
  setPathValue,
} from "./config/form-utils.ts";

export type ConfigState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  applySessionKey: string;
  configLoading: boolean;
  configRaw: string;
  configRawOriginal: string;
  configValid: boolean | null;
  configIssues: unknown[];
  configSaving: boolean;
  configApplying: boolean;
  updateRunning: boolean;
  configSnapshot: ConfigSnapshot | null;
  configSchema: unknown;
  configSchemaVersion: string | null;
  configSchemaLoading: boolean;
  configUiHints: ConfigUiHints;
  configForm: Record<string, unknown> | null;
  configFormOriginal: Record<string, unknown> | null;
  configFormDirty: boolean;
  configFormMode: "form" | "raw";
  configSearchQuery: string;
  configActiveSection: string | null;
  configActiveSubsection: string | null;
  lastError: string | null;
  /** Set when operator explicitly changes instance default; cleared after save. */
  configAllowInstanceDefaultChange?: boolean;
};

export async function loadConfig(state: ConfigState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.configLoading = true;
  state.lastError = null;
  try {
    const res = await state.client.request<ConfigSnapshot>("config.get", {});
    applyConfigSnapshot(state, res);
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.configLoading = false;
  }
}

export async function loadConfigSchema(state: ConfigState) {
  if (!state.client || !state.connected) {
    return;
  }
  if (state.configSchemaLoading) {
    return;
  }
  state.configSchemaLoading = true;
  try {
    const res = await state.client.request<ConfigSchemaResponse>("config.schema", {});
    applyConfigSchema(state, res);
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.configSchemaLoading = false;
  }
}

export function applyConfigSchema(state: ConfigState, res: ConfigSchemaResponse) {
  state.configSchema = res.schema ?? null;
  state.configUiHints = res.uiHints ?? {};
  state.configSchemaVersion = res.version ?? null;
}

export function applyConfigSnapshot(state: ConfigState, snapshot: ConfigSnapshot) {
  state.configSnapshot = snapshot;
  const rawFromSnapshot =
    typeof snapshot.raw === "string"
      ? snapshot.raw
      : snapshot.config && typeof snapshot.config === "object"
        ? serializeConfigForm(snapshot.config)
        : state.configRaw;
  if (!state.configFormDirty || state.configFormMode === "raw") {
    state.configRaw = rawFromSnapshot;
  } else if (state.configForm) {
    state.configRaw = serializeConfigForm(state.configForm);
  } else {
    state.configRaw = rawFromSnapshot;
  }
  state.configValid = typeof snapshot.valid === "boolean" ? snapshot.valid : null;
  state.configIssues = Array.isArray(snapshot.issues) ? snapshot.issues : [];

  if (!state.configFormDirty) {
    state.configForm = cloneConfigObject(snapshot.config ?? {});
    state.configFormOriginal = cloneConfigObject(snapshot.config ?? {});
    state.configRawOriginal = rawFromSnapshot;
  }
}

export async function saveConfig(
  state: ConfigState,
  opts?: { allowInstanceDefaultChange?: boolean },
) {
  if (!state.client || !state.connected) {
    return;
  }
  state.configSaving = true;
  state.lastError = null;
  try {
    const raw =
      state.configFormMode === "form" && state.configForm
        ? serializeConfigForm(state.configForm)
        : state.configRaw;
    const baseHash = state.configSnapshot?.hash;
    if (!baseHash) {
      state.lastError = "Config hash missing; reload and retry.";
      return;
    }
    const allowInstanceDefaultChange =
      opts?.allowInstanceDefaultChange === true || state.configAllowInstanceDefaultChange === true;
    await state.client.request("config.set", {
      raw,
      baseHash,
      ...(allowInstanceDefaultChange ? { allowInstanceDefaultChange: true } : {}),
    });
    state.configAllowInstanceDefaultChange = false;
    state.configFormDirty = false;
    await loadConfig(state);
    // Reload agents list after config change to pick up any new/removed agents
    await loadAgents(state as unknown as Parameters<typeof loadAgents>[0], { quiet: true });
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.configSaving = false;
  }
}

/** Merge a partial config object via gateway `config.patch` (requires baseHash). */
export async function patchConfig(
  state: ConfigState,
  patch: Record<string, unknown>,
  opts?: {
    allowInstanceDefaultChange?: boolean;
    sessionKey?: string;
    note?: string;
    restartDelayMs?: number;
  },
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  state.lastError = null;
  const baseHash = state.configSnapshot?.hash;
  if (!baseHash) {
    state.lastError = "Config hash missing; reload and retry.";
    return false;
  }
  try {
    const raw = serializeConfigForm(patch);
    const allowInstanceDefaultChange =
      opts?.allowInstanceDefaultChange === true || state.configAllowInstanceDefaultChange === true;
    await state.client.request("config.patch", {
      raw,
      baseHash,
      ...(opts?.sessionKey ? { sessionKey: opts.sessionKey } : {}),
      ...(opts?.note ? { note: opts.note } : {}),
      ...(opts?.restartDelayMs != null ? { restartDelayMs: opts.restartDelayMs } : {}),
      ...(allowInstanceDefaultChange ? { allowInstanceDefaultChange: true } : {}),
    });
    state.configAllowInstanceDefaultChange = false;
    state.configFormDirty = false;
    await loadConfig(state);
    // Reload agents list after config change to pick up any new/removed agents
    await loadAgents(state as unknown as Parameters<typeof loadAgents>[0], { quiet: true });
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export async function applyConfig(state: ConfigState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.configApplying = true;
  state.lastError = null;
  try {
    const raw =
      state.configFormMode === "form" && state.configForm
        ? serializeConfigForm(state.configForm)
        : state.configRaw;
    const baseHash = state.configSnapshot?.hash;
    if (!baseHash) {
      state.lastError = "Config hash missing; reload and retry.";
      return;
    }
    await state.client.request("config.apply", {
      raw,
      baseHash,
      sessionKey: state.applySessionKey,
    });
    state.configFormDirty = false;
    await loadConfig(state);
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.configApplying = false;
  }
}

export async function runUpdate(state: ConfigState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.updateRunning = true;
  state.lastError = null;
  try {
    await state.client.request("update.run", {
      sessionKey: state.applySessionKey,
    });
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.updateRunning = false;
  }
}

export function updateConfigFormValue(
  state: ConfigState,
  path: Array<string | number>,
  value: unknown,
) {
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  setPathValue(base, path, value);
  state.configForm = base;
  state.configFormDirty = true;
  if (state.configFormMode === "form") {
    state.configRaw = serializeConfigForm(base);
  }
}

export function removeConfigFormValue(state: ConfigState, path: Array<string | number>) {
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  removePathValue(base, path);
  state.configForm = base;
  state.configFormDirty = true;
  if (state.configFormMode === "form") {
    state.configRaw = serializeConfigForm(base);
  }
}

/** Clone the active config form, apply an in-place mutation, and mark dirty. */
export function applyConfigFormMutation(
  state: ConfigState,
  mutate: (config: Record<string, unknown>) => void,
): void {
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  mutate(base);
  state.configForm = base;
  state.configFormDirty = true;
  if (state.configFormMode === "form") {
    state.configRaw = serializeConfigForm(base);
  }
}

/**
 * Mark exactly one agent in `agents.list` as `default: true` and clear `default` on all others.
 * @returns true if the target agent id was found in the list
 */
export function setInstanceDefaultAgentInForm(state: ConfigState, targetIdRaw: string): boolean {
  const targetId = normalizeAgentId(targetIdRaw);
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  const agents = base.agents;
  if (!agents || typeof agents !== "object") {
    return false;
  }
  const list = (agents as Record<string, unknown>).list;
  if (!Array.isArray(list)) {
    return false;
  }
  let found = false;
  for (let i = 0; i < list.length; i += 1) {
    const entry = list[i];
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const id = normalizeAgentId((entry as { id?: unknown }).id as string | undefined);
    if (!id) {
      continue;
    }
    if (id === targetId) {
      setPathValue(base, ["agents", "list", i, "default"], true);
      found = true;
    } else {
      removePathValue(base, ["agents", "list", i, "default"]);
    }
  }
  if (!found) {
    return false;
  }
  state.configAllowInstanceDefaultChange = true;
  state.configForm = base;
  state.configFormDirty = true;
  if (state.configFormMode === "form") {
    state.configRaw = serializeConfigForm(base);
  }
  return true;
}
